import { ENV, Environment } from '@backoffice-frontend/environment'
import { SdsMode } from '@backoffice-frontend/graphql'
import type { AdVehicleStreamHubReturnVehicleFragment } from './graphql/HubReturnVehicle.hook'

// Hub return is rolled out per fleet, and fleet ids differ per environment, so
// the allow list is keyed by environment.
const eligibleFleets: Record<Environment, string[]> = {
  [Environment.test]: ['2c99a5aa-ae34-4fa2-8b6a-0e06fcf49daf'], // Hamburg Simulation Line Service
  [Environment.local]: [],
  [Environment.development]: [
    '2c99a5aa-ae34-4fa2-8b6a-0e06fcf49daf', // Hamburg Simulation Line Service
    'c35f10e4-b590-467e-a8fb-db609dabc23d', // Beep Line Service
  ],
  [Environment.integration]: [
    '21ff3309-892d-42ef-a205-defbd39fe164', // Hamburg Simulation Line Service
    '1a75583d-15ba-44c8-8069-c3dac8b5a8d9', // LN Line Service (with CSC & Passthrough)
    'eac42ff8-fe63-4a0e-9e1c-d8341947d6f8', // Beep Line Service
  ],
  [Environment.production]: [
    '1a75583d-15ba-44c8-8069-c3dac8b5a8d9', // LN Line Service (with CSC & Passthrough)
    '879abb56-f177-432b-9da6-5185f42b8ada', // Beep Line Service
  ],
  [Environment['production.us-east-1']]: [],
}

const isFleetEligibleForHubReturn = (vehicle: HubReturnVehicle): boolean =>
  Boolean(vehicle.fleet && eligibleFleets[ENV].includes(vehicle.fleet.id))

type HubReturnVehicle = AdVehicleStreamHubReturnVehicleFragment

const getUpcomingActions = (vehicle: HubReturnVehicle) =>
  vehicle.fccUpcomingWaypoints?.flatMap(waypoint => waypoint.actions) ?? []

const hasActiveTrips = (vehicle: HubReturnVehicle) =>
  getUpcomingActions(vehicle).some(
    action =>
      (action.__typename === 'FccWaypointPickupAction' ||
        action.__typename === 'FccWaypointDeliveryAction') &&
      Boolean(action.trip?.id),
  )

type HubReturnDisabledReason =
  | 'noPermission'
  | 'sdsNotEngaged'
  | 'notResponsive'
  | 'poolingEnabled'
  | 'hasActiveTrips'
  | 'fleetNotEligibleForHubReturn'

const getHubReturnDisabledReason = (
  vehicle: HubReturnVehicle,
  hasPermission: boolean,
): HubReturnDisabledReason | undefined => {
  if (!hasPermission) return 'noPermission'
  if (!isFleetEligibleForHubReturn(vehicle))
    return 'fleetNotEligibleForHubReturn'
  // sdsMode is nested under latestADTelemetry (ADTelemetry.sdsMode.sdsMode)
  if (vehicle.latestADTelemetry?.sdsMode?.sdsMode !== SdsMode.Engaged) {
    return 'sdsNotEngaged'
  }
  if (vehicle.isResponsive === false) return 'notResponsive'
  const poolingEnabled = vehicle.pooling?.enabled === true
  const activeTrips = hasActiveTrips(vehicle)
  if (poolingEnabled) return 'poolingEnabled'
  if (activeTrips) return 'hasActiveTrips'

  return undefined
}

export const isHubReturnEligible = (
  vehicle: HubReturnVehicle,
  hasPermission: boolean,
): boolean => getHubReturnDisabledReason(vehicle, hasPermission) === undefined

export const getHubReturnDisabledTooltip = (
  vehicle: HubReturnVehicle,
  hasPermission: boolean,
  t: (key: string) => string,
): string | undefined => {
  const reason = getHubReturnDisabledReason(vehicle, hasPermission)
  switch (reason) {
    case 'noPermission':
      return t('common_noPermission_tooltip')
    case 'poolingEnabled':
    case 'hasActiveTrips':
      return t('adFleetMap_tooltip_hubReturnDisabledGeneric_caption')
    case 'fleetNotEligibleForHubReturn':
      return t('adFleetMap_tooltip_hubReturnDisabledFleetNotEligible_caption')
    case 'sdsNotEngaged':
      return t('adFleetMap_tooltip_hubReturnDisabledSdsNotEngaged_caption')
    case 'notResponsive':
      return t('adFleetMap_tooltip_hubReturnDisabledNotResponsive_caption')
    default:
      return undefined
  }
}
